/**
 * ONE SPECTATE TARGET, HELD ACROSS ONE NAVIGATION.
 *
 * The mirror of `engine/modes/seat-handoff.ts`, for the social lane. The friends
 * list is the META chunk and the spectator surface is reached through the arena,
 * which is a different chunk behind a route change (ADR-0005): the social screen
 * may not import `engine/match/**` or mount the surface itself, so it stashes
 * WHAT to watch and navigates. `engine/bootstrap.ts` pops it on entry.
 *
 * ── WHY THIS FILE IMPORTS ONLY A TYPE ───────────────────────────────────────
 * `engine/bootstrap.ts` is the SHELL chunk (<= 150 KB gz, ADR-0005). It imports
 * `takePendingSpectate` from here, so this file must pull no social runtime into
 * first paint — exactly the property `seat-handoff.ts` has, and for the same
 * reason. It imports nothing but `type SpectatorTarget`, which is erased at
 * build; the spectator surface itself arrives through a dynamic `import()`.
 *
 * ── WHY `sessionStorage`, AND THE POP DISCIPLINE ────────────────────────────
 * Per tab, cleared when the tab closes, never sent to a server. A target is a
 * friend's user id or a public match id, NOT a credential — the spectate
 * endpoint checks privacy, blocking and "you are seated in this match" itself
 * and refuses all of them uniformly. It rides storage (not a module variable)
 * for the same reason the seat does: a shared link, a refresh or an external
 * return must not silently drop it. `takePendingSpectate()` is a POP so a stale
 * target cannot reopen an unrelated later visit to the arena on a match nobody
 * asked to watch; the in-memory fallback covers a browser that refuses storage.
 */
import type { SpectatorTarget } from './spectator-session';

/** The `sessionStorage` key. Per tab, per origin. */
export const PENDING_SPECTATE_KEY = 'rga.pendingSpectate';

/** The in-memory fallback for a browser that refuses storage. */
let inMemory: SpectatorTarget | null = null;

function storage(): Storage | null {
  try {
    // The property access ITSELF throws in some locked-down browsers, which is
    // why this is inside the try rather than an `in` check.
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Is this really a spectate target, and not a leftover from an older build? */
function isTarget(value: unknown): value is SpectatorTarget {
  if (value === null || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  if (obj.kind === 'player') return typeof obj.userId === 'string' && obj.userId !== '';
  if (obj.kind === 'match') return typeof obj.matchId === 'string' && obj.matchId !== '';
  return false;
}

/** Remember what the player asked to watch. Replaces any earlier one. */
export function stashPendingSpectate(target: SpectatorTarget): void {
  inMemory = target;
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(PENDING_SPECTATE_KEY, JSON.stringify(target));
  } catch {
    /* quota, private mode, a disabled store — the in-memory copy stands */
  }
}

/**
 * TAKE the pending target, if there is one. A pop, not a read.
 *
 * The arena calls this once on entry: leaving it behind would make a later,
 * unrelated visit to the arena reopen a spectator surface the player did not ask
 * for.
 */
export function takePendingSpectate(): SpectatorTarget | null {
  const held = inMemory;
  inMemory = null;
  const store = storage();
  if (store === null) return held;
  let raw: string | null = null;
  try {
    raw = store.getItem(PENDING_SPECTATE_KEY);
    store.removeItem(PENDING_SPECTATE_KEY);
  } catch {
    return held;
  }
  if (raw === null) return held;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isTarget(parsed) ? parsed : held;
  } catch {
    return held;
  }
}

/** Look without taking — for a screen that wants to say "you asked to watch". */
export function peekPendingSpectate(): SpectatorTarget | null {
  const store = storage();
  if (store === null) return inMemory;
  try {
    const raw = store.getItem(PENDING_SPECTATE_KEY);
    if (raw === null) return inMemory;
    const parsed: unknown = JSON.parse(raw);
    return isTarget(parsed) ? parsed : inMemory;
  } catch {
    return inMemory;
  }
}

/** Drop it — the player went somewhere else. */
export function clearPendingSpectate(): void {
  inMemory = null;
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(PENDING_SPECTATE_KEY);
  } catch {
    /* nothing to do: there is no state we could be leaving wrong */
  }
}
